import {
  forwardRef,
  useImperativeHandle,
  useRef,
  cloneElement,
  ReactElement,
} from 'react'
import generatePDF, { Margin } from 'react-to-pdf'

export type PdfWrapperHandle = { generatePDF: () => void }

/*
This component facilitates the generation of PDF documents from its child content.
It accomplishes this by creating an invisible container for the child content and 
uses the 'react-to-pdf' library to convert it into a PDF document.

Example Usage:
    const pdfRef = useRef<PdfWrapperHandle>(null);
    const downloadPdf = () => {
        pdfRef.current?.generatePDF();
    }
    return (
        <Button onClick={downloadPdf} />
        <PdfWrapper ref={pdfRef} filename="example.pdf" width={1000}>
            <ExampleComponent />
        </PdfWrapper>
    )
*/
export const PdfWrapper = forwardRef(
  (
    {
      children,
      filename,
      width = 1100,
      // If true then this component won't be mirrored on screen
      hideComponent = false,
      // There is a natural border around content but if there are components that need to be flush with edges this should be set to true
      hideMargin = false,
      // Text stamped onto the bottom of every page, instead of flowing as regular content that can be split across a page break
      footerText,
    }: {
      children: ReactElement
      filename: string
      width?: number
      hideComponent?: boolean
      hideMargin?: boolean
      footerText?: string
    },
    ref
  ) => {
    const pdfRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => ({
      generatePDF: async () => {
        const margin = hideMargin ? Margin.NONE : Margin.SMALL

        const pageOptions = {
          filename,
          page: { margin },
        }

        if (!footerText) {
          return generatePDF(pdfRef, pageOptions)
        }

        const pdf = await generatePDF(pdfRef, {
          ...pageOptions,
          method: 'build',
        })
        const pageCount = pdf.getNumberOfPages()
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()

        for (let page = 1; page <= pageCount; page++) {
          pdf.setPage(page)
          pdf.setFontSize(8)
          pdf.text(footerText, pageWidth - 6, pageHeight - 6, {
            align: 'right',
          })
        }

        return pdf.save(filename, { returnPromise: true })
      },
    }))

    return (
      <>
        {!hideComponent && children}
        <div
          ref={pdfRef}
          className="pdf-export"
          style={{
            position: 'absolute',
            left: -9999,
            width,
          }}
        >
          {cloneElement(children, { isPdf: true })}
        </div>
      </>
    )
  }
)
